import { PropertiesPanelControl, PropertiesPanelLabel, PropertiesPanelProperty } from './propertiesPanelProperty';
import { BasicButton } from 'src/components/widgets/buttons';
import React from 'react';
import { Icon } from 'src/components/widgets/icon';
import { styled } from 'styled-components';
import { RenameIcon as PencilSolid, ResetIcon as ResetSolid, TrashIcon as TrashSolid } from 'src/components/widgets/icons/scene';

export type PropertiesPanelInlineIconPropertyProps = {
    id: string;
    label: React.ReactNode;
    image: string | null;
    baseImage?: React.ReactNode;
    actionIconsSize?: number;
    onImagePick?: (image: string | null) => void;
    disabled?: boolean;
    showDeleteButton?: boolean;
};

export const PropertiesPanelInlineIconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

export const PropertiesPanelInlineIconActions = styled.div<{ $actionIconsSize?: number }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 5px;
    & > * {
        width: ${(props) => props.$actionIconsSize}px;
        height: ${(props) => props.$actionIconsSize}px;
    }
`;

export const PropertiesPanelInlineIcon = styled.img`
    min-width: 24px;
    min-height: 24px;
    max-width: 32px;
    max-height: 32px;
`;

export const PropertiesPanelInlineIconButton = styled(BasicButton)<{ $hasImage: boolean }>`
    min-height: unset;
    min-width: unset;
    font-size: 12px;
    padding: ${(props) => (props.$hasImage ? '4px' : '10px')};
    border-radius: 4px;
    border-color: ${(props) => props.theme.app.borderColor};
    cursor: pointer;
    &:hover:not([disabled]) {
        background-color: ${(props) => props.theme.app.borderColor};
    }
`;

export const PropertiesPanelInlineIconProperty: React.FC<PropertiesPanelInlineIconPropertyProps> = ({
    id,
    label,
    image,
    baseImage,
    onImagePick,
    actionIconsSize,
    disabled,
    showDeleteButton = true
}) => {
    const [pickedImage, setPickedImage] = React.useState<string | null>(image);

    const fireImagePick = (img: string | null) => {
        if (disabled) return;
        setPickedImage(img);
        onImagePick?.(img);
    };

    const openImageFile = () => {
        if (disabled) return;
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.addEventListener('change', async (e) => {
            const files = (e.currentTarget as HTMLInputElement).files;
            if (files && files.length > 0) {
                const file = files[0];
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    fireImagePick(reader.result as string);
                    input.remove();
                };
            }
        });
        document.body.appendChild(input);
        input.click();
    };

    const getIcon = () => {
        if (pickedImage === 'base') {
            return baseImage;
        } else {
            return <PropertiesPanelInlineIcon src={pickedImage!}></PropertiesPanelInlineIcon>;
        }
    };

    return (
        <PropertiesPanelProperty>
            <PropertiesPanelLabel htmlFor={`${id}-input`}>{label}</PropertiesPanelLabel>
            <PropertiesPanelControl>
                <PropertiesPanelInlineIconContainer>
                    <PropertiesPanelInlineIconButton $hasImage={!!pickedImage} onClick={openImageFile} disabled={disabled}>
                        {!!pickedImage && getIcon()}
                    </PropertiesPanelInlineIconButton>
                    <PropertiesPanelInlineIconActions $actionIconsSize={actionIconsSize}>
                        <Icon onClick={openImageFile} disabled={disabled} $hoverable>
                            <PencilSolid />
                        </Icon>
                        <Icon onClick={() => fireImagePick('base')} disabled={disabled} $hoverable>
                            <ResetSolid />
                        </Icon>
                        {showDeleteButton && (
                            <Icon onClick={() => fireImagePick(null)} disabled={disabled} $hoverable>
                                <TrashSolid />
                            </Icon>
                        )}
                    </PropertiesPanelInlineIconActions>
                </PropertiesPanelInlineIconContainer>
            </PropertiesPanelControl>
        </PropertiesPanelProperty>
    );
};
